// Registry for the role-based user manuals. Each manual is a plain data
// object (see any of the ./*.js files) rendered by UserManualPage.jsx.
//
// Block shapes a section's `blocks` array may contain:
//   'text'                              → paragraph
//   { sub: 'text' }                     → subheading
//   { list: ['a', 'b'] }                → bulleted list
//   { steps: ['a', 'b'] }               → numbered list
//   { table: { head: [...], rows: [[...]] } }
//   { note: 'text' }                    → highlighted callout
//   { verify: 'text' }                  → "Needs verification" callout
//   { image: '/manual/x.png', caption?: 'text', alt?: 'text' }
//                                       → screenshot / illustration. Drop the
//                                         file in `public/manual/` (see the
//                                         README there) and reference it by its
//                                         app-root path. `caption` shows below
//                                         and is used as alt text when `alt`
//                                         is omitted. Renders on-screen AND in
//                                         the printed / Save-as-PDF manual.

import superAdmin from './super-admin'
import companyAdmin from './company-admin'
import subAdmin from './sub-admin'
import user from './user'

const BY_KEY = {
  super_admin: superAdmin,
  company_admin: companyAdmin,
  sub_admin: subAdmin,
  user,
}

/**
 * Resolve the correct manual for the signed-in user. Role decides the manual.
 * External members (membershipType === 'external', on the `user` role) read the
 * SAME Member manual as internal users — Managers, Team Leaders and Employees —
 * rather than a separate scope-limited guide.
 */
export function manualForUser(user) {
  if (!user) return BY_KEY.user
  return BY_KEY[user.role] ?? BY_KEY.user
}

/** Flatten a block's visible text — used for in-manual search. */
export function blockText(block) {
  if (typeof block === 'string') return block
  if (!block || typeof block !== 'object') return ''
  if (block.sub) return block.sub
  if (block.note) return block.note
  if (block.verify) return block.verify
  if (block.list) return block.list.join(' ')
  if (block.steps) return block.steps.join(' ')
  if (block.table) {
    const head = block.table.head ?? []
    const rows = (block.table.rows ?? []).flat()
    return [...head, ...rows].join(' ')
  }
  return ''
}

/** All searchable text for one section (title + every block). */
export function sectionText(section) {
  return [section.title, ...section.blocks.map(blockText)].join(' ')
}

export default BY_KEY
